// client/src/context/AuthProvider.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import ApiAuth from '../api/ApiAuth'
import AuthContext from './AuthContext'
import { isAdmin as isAdminRole } from '../utils/userRoles'
import { setAuthFailureHandler } from '../api/FlexFetch'

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  // Guard against React 18 StrictMode double-invoke and unmount updates
  const hasFetchedRef = useRef(false)
  const isMountedRef = useRef(false)

  // Determine if current route is a pure public page that doesn't need auth
  // These pages work identically whether the user is logged in or not
  const isPurePublicRoute = useMemo(() => {
    const purePublicRoutes = [
      '/interest', // Lead collection - no auth needed
      '/terms-of-service', // Legal docs - no auth needed
      '/privacy-policy', // Legal docs - no auth needed
      '/founders', // Public company page - no auth needed
      '/sms-notifications-disclosure', // Legal docs - no auth needed
      '/confirm-partner', // Partner invite confirm/decline - no auth needed
    ]
    // Note: /landing, /sign-in, /sign-up are NOT pure public because they
    // may want to check if user is already logged in and redirect accordingly
    return purePublicRoutes.some((route) => location.pathname.startsWith(route))
  }, [location.pathname])

  // Register global auth failure handler
  useEffect(() => {
    const handleAuthFailure = () => {
      // Only proceed if component is still mounted
      if (!isMountedRef.current) return

      // Clear user state
      setUser(null)

      // Don't redirect if already on public routes
      const publicRoutes = [
        '/landing',
        '/sign-in',
        '/sign-up',
        '/forgot-password',
        '/reset-password',
        '/verify-email',
        '/verify-email-pending',
        '/terms-of-service',
        '/privacy-policy',
        '/founders',
        '/sms-notifications-disclosure',
        '/interest',
        '/confirm-partner',
      ]
      if (publicRoutes.includes(location.pathname)) {
        return
      }

      // Navigate to landing page for protected routes
      navigate('/landing', { replace: true })
    }

    setAuthFailureHandler(handleAuthFailure)

    // Cleanup on unmount
    return () => {
      setAuthFailureHandler(null)
    }
  }, [navigate, location])

  useEffect(() => {
    isMountedRef.current = true
    if (hasFetchedRef.current) return

    // Skip user fetch for truly public pages that never need auth
    if (isPurePublicRoute) {
      setLoading(false)
      return
    }

    hasFetchedRef.current = true
    const abortController = new AbortController()

    async function fetchUserWithRetry(retryCount = 0, maxRetries = 3) {
      // Track whether we plan to retry in this invocation; if true, don't flip loading to false yet
      let willRetry = false
      try {
        const currentUser = await ApiAuth.me({
          signal: abortController.signal,
        })
        if (!isMountedRef.current) return
        setUser(currentUser)
      } catch (err) {
        if (!isMountedRef.current) return
        if (err && err.name === 'AbortError') {
          // Avoid flipping loading=false in finally when we intentionally aborted
          willRetry = true
          return
        }

        // Check if this is an authentication failure (401) vs network/server error
        const message = (err && err.message) || ''
        const isAuthFailure =
          (err.response && err.response.status === 401) ||
          /unauthorized|not authorized|not authenticated|forbidden/i.test(
            message,
          )
        const isNetworkError =
          !err.response || // Network error (no response)
          (err.response && err.response.status >= 500) || // Server error
          err.name === 'TypeError' // Network/fetch error

        // If it's a clear authentication failure (401), don't retry
        if (isAuthFailure) {
          setUser(null)
          return
        }

        // If it's a network error and we haven't exceeded retries, retry after delay
        if (isNetworkError && retryCount < maxRetries) {
          if (!isMountedRef.current) return

          // Exponential backoff: 500ms, 1s, 2s
          const delay = Math.pow(2, retryCount) * 500
          willRetry = true
          setTimeout(() => {
            if (isMountedRef.current) {
              fetchUserWithRetry(retryCount + 1, maxRetries)
            }
          }, delay)
          return
        }

        // Fallback: if environment doesn't like the signal option, retry once without it
        if (
          retryCount === 0 &&
          err instanceof TypeError &&
          (message.includes('signal') || message.includes('AbortSignal'))
        ) {
          try {
            const currentUser = await ApiAuth.me()
            if (!isMountedRef.current) return
            setUser(currentUser)
            return
          } catch (_retryErr) {
            // Continue to handle this as a regular error
            if (!isMountedRef.current) return
            const isRetryAuthFailure =
              _retryErr.response && _retryErr.response.status === 401
            if (isRetryAuthFailure) {
              console.log('Authentication failed on retry - user not logged in')
              setUser(null)
            } else if (retryCount < maxRetries) {
              // Retry without signal if it's still a network error
              willRetry = true
              // Use the same exponential backoff as primary path: 500ms, 1s, 2s
              const delay = Math.pow(2, retryCount) * 500
              setTimeout(() => {
                if (isMountedRef.current) {
                  fetchUserWithRetry(retryCount + 1, maxRetries)
                }
              }, delay)
              return
            } else {
              setUser(null)
            }
          }
        } else {
          // All retries exhausted or other error
          setUser(null)
        }
      } finally {
        // Only set loading to false if we're not going to retry and component is still mounted
        if (isMountedRef.current && !willRetry) {
          setLoading(false)
        }
      }
    }

    fetchUserWithRetry()
    return () => {
      isMountedRef.current = false
      // Reset the ref so the second StrictMode mount (the real one) runs the fetch
      hasFetchedRef.current = false
      abortController.abort()
    }
  }, [isPurePublicRoute])

  // Allow consumers to refresh the user after login or other auth changes
  // Wrap in useCallback to prevent unnecessary re-renders and infinite loops in components that depend on it
  const refreshUser = useCallback(async () => {
    try {
      setLoading(true)
      const currentUser = await ApiAuth.me()
      setUser(currentUser)
      return currentUser
    } catch (err) {
      setUser(null)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await ApiAuth.logout()
      setUser(null)
      // Note: Navigation will be handled by the component that calls logout
      // This keeps the context provider navigation-agnostic
    } catch (err) {
      console.error('Logout failed:', err)
      throw err // Re-throw so calling component can handle
    }
  }, [])

  const isLoggedIn = !!user
  const isAdmin = isAdminRole(user)

  const value = {
    user,
    loading,
    isLoggedIn,
    isAdmin,
    refreshUser,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider
